import { useState, type ReactNode } from 'react'
import type { ViewType } from '@/App'
import { useGameStore } from '@/store/gameState'
import { DebugMenu } from './DebugMenu'
import { ActivityLogModal } from './ActivityLogModal'
import styles from './Layout.module.css'

interface LayoutProps {
  children: ReactNode
  activeView: ViewType
  onViewChange: (view: ViewType) => void
}

export function Layout({ children, activeView, onViewChange }: LayoutProps) {
  const getEnergy = useGameStore((s) => s.getEnergy)
  const { total } = getEnergy()
  const [showActivityLog, setShowActivityLog] = useState(false)

  return (
    <div className={styles.app}>
      <header className={styles.header}>
        <div className={styles.brand}>
          <span className={styles.logo}>ECHOWILDS</span>
        </div>
        <div className={styles.stats}>
          <button
            className={styles.energyBadge}
            onClick={() => setShowActivityLog(true)}
            aria-label="View activity log"
          >
            <span className={styles.energyIcon}>⚡</span>
            <span className={styles.energyValue}>{total}</span>
          </button>
        </div>
      </header>

      {showActivityLog && (
        <ActivityLogModal onClose={() => setShowActivityLog(false)} />
      )}

      <nav className={styles.nav}>
        <button
          className={`${styles.navBtn} ${activeView === 'focus' ? styles.active : ''}`}
          onClick={() => onViewChange('focus')}
          aria-label="Focus"
        >
          <svg className={styles.navIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <circle cx="12" cy="12" r="9" />
            <path d="M12 7v5l3 3" strokeLinecap="round" />
          </svg>
          <span className={styles.navLabel}>Focus</span>
        </button>

        <button
          className={`${styles.navBtn} ${activeView === 'explore' ? styles.active : ''}`}
          onClick={() => onViewChange('explore')}
          aria-label="Explore"
        >
          <svg className={styles.navIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M12 2L2 7l10 5 10-5-10-5z" />
            <path d="M2 17l10 5 10-5" />
            <path d="M2 12l10 5 10-5" />
          </svg>
          <span className={styles.navLabel}>Explore</span>
        </button>

        <button
          className={`${styles.navBtn} ${activeView === 'codex' ? styles.active : ''}`}
          onClick={() => onViewChange('codex')}
          aria-label="Codex"
        >
          <svg className={styles.navIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
            <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
            <circle cx="12" cy="10" r="3" />
          </svg>
          <span className={styles.navLabel}>Codex</span>
        </button>
      </nav>

      <main className={styles.main}>
        {children}
      </main>

      <footer className={styles.footer}>
        <ResetButton />
        <DebugMenu />
      </footer>
    </div>
  )
}

function ResetButton() {
  const resetProgress = useGameStore((s) => s.resetProgress)

  const handleReset = () => {
    if (confirm('Reset all progress? This cannot be undone.')) {
      resetProgress()
    }
  }

  return (
    <button className={styles.resetBtn} onClick={handleReset}>
      Reset Progress
    </button>
  )
}
